// Mono 16-bit PCM WAV. encodeWav builds a 44-byte header + raw Int16 LE payload;
// parseWav validates the RIFF/WAVE magic and returns the PCM as Int16Array.
export function encodeWav(samples: Int16Array, sampleRate: number): ArrayBuffer {
  const channels = 1, bitsPerSample = 16;
  const dataBytes = samples.byteLength;
  const buf = new ArrayBuffer(44 + dataBytes);
  const v = new DataView(buf);
  const byteRate = (sampleRate * channels * bitsPerSample) / 8;
  const blockAlign = (channels * bitsPerSample) / 8;
  v.setUint32(0, 0x52494646, false);   // "RIFF"
  v.setUint32(4, 36 + dataBytes, true);
  v.setUint32(8, 0x57415645, false);   // "WAVE"
  v.setUint32(12, 0x666d7420, false);  // "fmt "
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);            // PCM
  v.setUint16(22, channels, true);
  v.setUint32(24, sampleRate, true);
  v.setUint32(28, byteRate, true);
  v.setUint16(32, blockAlign, true);
  v.setUint16(34, bitsPerSample, true);
  v.setUint32(36, 0x64617461, false);  // "data"
  v.setUint32(40, dataBytes, true);
  new Int16Array(buf, 44).set(samples);
  return buf;
}

export function parseWav(buf: ArrayBuffer): { samples: Int16Array; sampleRate: number; channels: number; bitsPerSample: number } {
  if (buf.byteLength < 44) throw new Error('wav: too small');
  const v = new DataView(buf);
  if (v.getUint32(0, false) !== 0x52494646 || v.getUint32(8, false) !== 0x57415645) {
    throw new Error('wav: not a RIFF/WAVE file');
  }
  // fmt chunk fields at their canonical offsets (same assumption as sampleRate@24).
  const channels = v.getUint16(22, true);
  const sampleRate = v.getUint32(24, true);
  const bitsPerSample = v.getUint16(34, true);
  // Walk chunks to find "data" (some encoders insert chunks before it).
  let off = 12;
  while (off + 8 <= buf.byteLength) {
    const id = v.getUint32(off, false);
    const size = v.getUint32(off + 4, true);
    if (id === 0x64617461) {
      if (off + 8 + size > buf.byteLength) throw new Error('wav: data chunk extends past buffer');
      const payload = buf.slice(off + 8, off + 8 + size);
      return { samples: new Int16Array(payload), sampleRate, channels, bitsPerSample };
    }
    off += 8 + size + (size & 1); // chunks are word-aligned
  }
  throw new Error('wav: no data chunk');
}
